import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { PageLayout } from "@/components/layout/PageLayout";
import { ProjectScopedFolderView } from "@/domains/projects/components/ProjectScopedFolderView";
import { usePermissions } from "@/hooks/usePermissions";
import { supabase } from "@/integrations/supabase/client";
import { useMarkProjectActivitySeen } from "@/hooks/projects/useProjectActivity";
import { useDocumentStore } from "@/hooks/documents/useDocumentStore";
import { useRbacReadiness } from "@/hooks/useRbacReadiness";
import { AccessReadiness } from "@/components/auth/AccessReadiness";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Canonical project route: /projects/:id
 *
 * Loads the project row (RLS filters to projects the caller can see) and renders
 * ProjectScopedFolderView. Subcontractors land here as their primary workspace,
 * but owners can also use this URL (the building Files page flow is
 * retained for building-context navigation).
 */
export const ProjectDetail = observer(function ProjectDetail() {
  const { id } = useParams<{ id: string }>();
  const projectIdNum = Number(id);
  const { canReadProject } = usePermissions();

  const rbac = useRbacReadiness();
  const { t } = useLanguage();
  // canReadProject() is false until permissions load, so the lookup waits for
  // them and its result is keyed to the access context it was made under — a
  // deep link must not bounce to /buildings on the first render.
  const requestKey = `${rbac.accessKey}:${projectIdNum}`;

  const [result, setResult] = useState<{
    requestKey: string;
    project: { id: number; name: string; building_id: number } | null;
  } | null>(null);
  const loading = result?.requestKey !== requestKey;
  const project = loading ? null : (result?.project ?? null);

  useEffect(() => {
    if (!id || !rbac.ready || !rbac.orgId) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("building_projects")
        .select("id, name, building_id")
        .eq("id", projectIdNum)
        .eq("org_id", rbac.orgId)
        .maybeSingle();
      if (cancelled) return;
      setResult({ requestKey, project: error ? null : data });
    })();
    return () => {
      cancelled = true;
    };
  }, [id, projectIdNum, rbac.ready, rbac.orgId, requestKey]);

  const documentStore = useDocumentStore();
  // Clears this project's Documents dot on the All buildings page.
  useMarkProjectActivitySeen(
    project ? project.id : null,
    "documents",
    !!project && !documentStore.isLoadingProjectDocs && !documentStore.error
  );

  if (!id) {
    return <Navigate to="/buildings" replace />;
  }

  if (rbac.noOrganization) return <Navigate to="/buildings" replace />;
  if (!rbac.ready) {
    return (
      <PageLayout>
        <AccessReadiness
          failed={rbac.failed}
          retry={rbac.retry}
          message={t.auth.permissionLoadFailed}
          loadingLabel="Loading project…"
        />
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <PageLayout>
        <div className="p-6 text-muted-foreground">Loading project…</div>
      </PageLayout>
    );
  }

  // Project not found (RLS filtered it out or id is wrong) or user lacks permission.
  if (!project || !canReadProject(projectIdNum)) {
    return <Navigate to="/buildings" replace />;
  }

  return (
    <PageLayout showNavigationButtons customPadding>
      <div className="mt-20 flex h-[calc(100vh-80px)] w-full overflow-hidden border-t">
        <ProjectScopedFolderView
          buildingId={String(project.building_id)}
          projectId={String(project.id)}
          projectName={project.name}
        />
      </div>
    </PageLayout>
  );
});
